import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { DaySchedule, WeeklySchedule } from '../entity';
import { TimeSlot } from '@features/visits/schedule/entity';

type WeekdayEditDialogProps = {
  open: boolean;
  onClose: () => void;
  weekday: keyof WeeklySchedule;
  currentSchedule: DaySchedule;
  onSave: (daySchedule: DaySchedule) => void;
};

const WeekdayEditDialog = ({
  open,
  onClose,
  weekday,
  currentSchedule,
  onSave,
}: WeekdayEditDialogProps) => {
  const intl = useIntl();
  const [isWorking, setIsWorking] = useState(true);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ start: '08:00', end: '20:00' }]);

  useEffect(() => {
    if (open) {
      setIsWorking(currentSchedule.isWorking);
      setTimeSlots(
        currentSchedule.timeSlots && currentSchedule.timeSlots.length > 0
          ? currentSchedule.timeSlots
          : [{ start: '08:00', end: '20:00' }],
      );
    }
  }, [open, currentSchedule]);

  const handleAddTimeSlot = () => {
    setTimeSlots([...timeSlots, { start: '08:00', end: '20:00' }]);
  };

  const handleRemoveTimeSlot = (index: number) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const handleTimeSlotChange = (index: number, field: 'start' | 'end', value: string) => {
    const updated = [...timeSlots];
    updated[index][field] = value;
    setTimeSlots(updated);
  };

  const handleSave = () => {
    const daySchedule: DaySchedule = {
      isWorking,
      timeSlots: isWorking ? timeSlots : [],
    };
    onSave(daySchedule);
    onClose();
  };

  const weekdayLabel = intl.formatMessage({ id: `weekday.${weekday}` });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="workSchedule.editWorkingHoursForWeekday"
          defaultMessage="Pracovní hodiny pro všechny {dayName}"
          values={{ dayName: weekdayLabel }}
        />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <FormattedMessage
              id="workSchedule.editWorkingHoursWarning"
              defaultMessage="Změna ovlivní všechny {dayName} v měsíci"
              values={{ dayName: weekdayLabel }}
            />
          </Alert>

          <FormControlLabel
            control={<Switch checked={isWorking} onChange={e => setIsWorking(e.target.checked)} />}
            label={<FormattedMessage id="workSchedule.isWorking" defaultMessage="Pracovní den" />}
          />

          {isWorking && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <FormattedMessage id="workSchedule.workingHours" defaultMessage="Pracovní hodiny" />
              </Typography>
              <Stack spacing={2}>
                {timeSlots.map((slot, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <TextField
                      type="time"
                      label={<FormattedMessage id="workSchedule.from" defaultMessage="Od" />}
                      value={slot.start}
                      onChange={e => handleTimeSlotChange(index, 'start', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="time"
                      label={<FormattedMessage id="workSchedule.to" defaultMessage="Do" />}
                      value={slot.end}
                      onChange={e => handleTimeSlotChange(index, 'end', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    {timeSlots.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTimeSlot(index)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                <Button
                  startIcon={<Add />}
                  onClick={handleAddTimeSlot}
                  variant="outlined"
                  size="small"
                >
                  <FormattedMessage
                    id="workSchedule.addTimeSlot"
                    defaultMessage="Přidat časový úsek"
                  />
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage id="common.cancel" defaultMessage="Zrušit" />
        </Button>
        <Button onClick={handleSave} variant="contained">
          <FormattedMessage id="common.save" defaultMessage="Uložit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeekdayEditDialog;
